import { useEffect, useMemo, useState, type FormEvent, type WheelEvent } from 'react';
import {
  createAsignacionInsumosVehiculo,
  getInsumos,
  updateAsignacionInsumosVehiculo,
} from '../../lib';
import { getVehiculos } from '../../lib/storage';
import type {
  AsignacionInsumosVehiculo,
  Insumo,
  UpsertAsignacionInsumosVehiculoInput,
  Vehiculo,
} from '../../lib/appTypes';

interface Props {
  initialAsignacion?: AsignacionInsumosVehiculo | null;
  onSaved: (asignacion: AsignacionInsumosVehiculo) => void;
  onCancel: () => void;
}

type DetalleForm = {
  insumo: string;
  cantidad: string;
  precioUnitario: string;
};

function preventNumberInputWheelChange(event: WheelEvent<HTMLInputElement>): void {
  event.currentTarget.blur();
}

function formatDateTimeLocal(value?: string): string {
  if (!value) {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().slice(0, 16);
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value.slice(0, 16);
  }

  parsed.setMinutes(parsed.getMinutes() - parsed.getTimezoneOffset());
  return parsed.toISOString().slice(0, 16);
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('es-EC', {
    style: 'currency',
    currency: 'USD',
  }).format(value);
}

function formatQuantity(value: number): string {
  return new Intl.NumberFormat('es-EC', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value);
}

function extractApiErrorMessage(err: unknown, fallback: string): string {
  const apiError = err as {
    message?: string;
    data?: {
      errors?: Record<string, unknown>;
    };
  };

  const errors = apiError?.data?.errors;
  if (errors) {
    const firstValue = Object.values(errors)[0];
    if (typeof firstValue === 'string' && firstValue.trim()) {
      return firstValue;
    }
    if (Array.isArray(firstValue) && typeof firstValue[0] === 'string') {
      return firstValue[0];
    }
  }

  return apiError?.message ?? fallback;
}

export default function FormAsignacionInsumosVehiculo({
  initialAsignacion,
  onSaved,
  onCancel,
}: Props) {
  const [form, setForm] = useState({
    vehiculo: initialAsignacion?.vehiculo ?? '',
    valorTotalAsignado: initialAsignacion?.valorTotalAsignado ?? '',
    fechaValorTotalAsignado: formatDateTimeLocal(initialAsignacion?.fechaValorTotalAsignado),
    observacion: initialAsignacion?.observacion ?? '',
    responsable: initialAsignacion?.responsable ?? '',
    kilometraje: initialAsignacion?.kilometraje != null ? String(initialAsignacion.kilometraje) : '',
  });

  const [detalles, setDetalles] = useState<DetalleForm[]>(
    initialAsignacion?.detalles?.length
      ? initialAsignacion.detalles.map(detalle => ({
          insumo: detalle.insumo,
          cantidad: String(detalle.cantidad ?? ''),
          precioUnitario: detalle.precioUnitario == null ? '' : String(detalle.precioUnitario),
        }))
      : [{ insumo: '', cantidad: '1', precioUnitario: '' }],
  );

  const [detallePage, setDetallePage] = useState(1);
  const DETALLE_PAGE_SIZE = 10;

  const detalleTotalPages = Math.max(1, Math.ceil(detalles.length / DETALLE_PAGE_SIZE));
  const detallePageSafe = Math.min(detallePage, detalleTotalPages);
  const detalleSlice = detalles.slice((detallePageSafe - 1) * DETALLE_PAGE_SIZE, detallePageSafe * DETALLE_PAGE_SIZE);

  useEffect(() => {
    if (detallePage > detalleTotalPages) {
      setDetallePage(detalleTotalPages);
    }
  }, [detalles.length, detalleTotalPages, detallePage]);

  const [vehiculos, setVehiculos] = useState<Vehiculo[]>([]);
  const [insumos, setInsumos] = useState<Insumo[]>([]);
  const [loadingData, setLoadingData] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const isEditing = Boolean(initialAsignacion?.id);

  useEffect(() => {
    let cancelled = false;

    async function run() {
      try {
        setLoadingData(true);
        const [nextVehiculos, nextInsumos] = await Promise.all([
          getVehiculos(),
          getInsumos({ page_size: 500, order_by: 'nombre', order_up: true }),
        ]);

        if (!cancelled) {
          setVehiculos(nextVehiculos);
          setInsumos(nextInsumos);
        }
      } catch {
        if (!cancelled) {
          setError('No se pudieron cargar vehículos o insumos.');
        }
      } finally {
        if (!cancelled) {
          setLoadingData(false);
        }
      }
    }

    void run();

    return () => {
      cancelled = true;
    };
  }, []);

  const insumosById = useMemo(() => {
    return new Map(insumos.map(insumo => [insumo.id, insumo]));
  }, [insumos]);

  const resumen = useMemo(() => {
    const totalAsignado = Number(form.valorTotalAsignado || 0);

    const totalInsumos = detalles.reduce((total, detalle) => {
      const cantidad = Number(detalle.cantidad || 0);
      const insumo = insumosById.get(detalle.insumo);
      const precioUnitario = detalle.precioUnitario === ''
        ? Number(insumo?.precio ?? 0)
        : Number(detalle.precioUnitario || 0);

      if (!Number.isFinite(cantidad) || !Number.isFinite(precioUnitario)) return total;

      return total + cantidad * precioUnitario;
    }, 0);

    return {
      totalAsignado,
      totalInsumos,
      saldoDisponible: totalAsignado - totalInsumos,
    };
  }, [detalles, form.valorTotalAsignado, insumosById]);

  function handle(event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) {
    const { name, value } = event.target;
    setForm(prev => ({ ...prev, [name]: value }));
  }

  function updateDetalle(index: number, field: keyof DetalleForm, value: string) {
    setDetalles(prev => prev.map((detalle, currentIndex) => {
      if (currentIndex !== index) return detalle;

      if (field === 'insumo') {
        return {
          ...detalle,
          insumo: value,
          precioUnitario: '',
        };
      }

      return {
        ...detalle,
        [field]: value,
      };
    }));
  }

  function addDetalle() {
    setDetalles(prev => [...prev, { insumo: '', cantidad: '1', precioUnitario: '' }]);
  }

  function removeDetalle(index: number) {
    setDetalles(prev => {
      if (prev.length === 1) return prev;
      return prev.filter((_, currentIndex) => currentIndex !== index);
    });
  }

  function getDetalleTotal(detalle: DetalleForm): number {
    const cantidad = Number(detalle.cantidad || 0);
    const insumo = insumosById.get(detalle.insumo);
    const precioUnitario = detalle.precioUnitario === ''
      ? Number(insumo?.precio ?? 0)
      : Number(detalle.precioUnitario || 0);

    if (!Number.isFinite(cantidad) || !Number.isFinite(precioUnitario)) return 0;

    return cantidad * precioUnitario;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError('');

    if (!form.vehiculo) {
      setError('Selecciona un vehículo.');
      return;
    }

    const valorTotalAsignado = Number(form.valorTotalAsignado);
    if (!Number.isFinite(valorTotalAsignado) || valorTotalAsignado < 0) {
      setError('Ingresa un valor total asignado válido.');
      return;
    }

    if (!form.fechaValorTotalAsignado) {
      setError('Selecciona la fecha del registro.');
      return;
    }

    if (detalles.length === 0) {
      setError('Agrega al menos un insumo.');
      return;
    }

    const detallesPayload = detalles.map((detalle, index) => {
      const cantidad = Number(detalle.cantidad);
      const precioUnitario = detalle.precioUnitario === '' ? undefined : Number(detalle.precioUnitario);

      if (!detalle.insumo) {
        throw new Error(`Selecciona el insumo en la línea ${index + 1}.`);
      }

      if (!Number.isFinite(cantidad) || cantidad <= 0) {
        throw new Error(`Ingresa una cantidad válida en la línea ${index + 1}.`);
      }

      if (precioUnitario !== undefined && (!Number.isFinite(precioUnitario) || precioUnitario < 0)) {
        throw new Error(`Ingresa un precio unitario válido en la línea ${index + 1}.`);
      }

      return {
        insumo: detalle.insumo,
        cantidad,
        precioUnitario,
      };
    });

    if (resumen.totalInsumos > valorTotalAsignado) {
      setError('El total de insumos no puede superar el valor total asignado al vehículo.');
      return;
    }

    const payload: UpsertAsignacionInsumosVehiculoInput = {
      vehiculo: form.vehiculo,
      valorTotalAsignado,
      fechaValorTotalAsignado: form.fechaValorTotalAsignado,
      observacion: form.observacion.trim(),
      responsable: form.responsable.trim(),
      kilometraje: form.kilometraje.trim() ? Number(form.kilometraje) : null,
      detalles: detallesPayload,
    };

    try {
      setLoading(true);
      const asignacion = initialAsignacion
        ? await updateAsignacionInsumosVehiculo(initialAsignacion.id, payload)
        : await createAsignacionInsumosVehiculo(payload);

      onSaved(asignacion);
    } catch (err: unknown) {
      setError(extractApiErrorMessage(err, 'No se pudo guardar la asignación de insumos.'));
    } finally {
      setLoading(false);
    }
  }

  const inputCls =
    'w-full rounded-2xl border border-stone-300 bg-white px-3 py-2.5 text-sm text-stone-700 outline-none transition focus:border-teal-500 focus:ring-4 focus:ring-teal-100';
  const labelCls = 'mb-1 block text-xs font-semibold uppercase tracking-[0.18em] text-stone-500';

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold text-stone-900">
          {isEditing ? 'Editar asignación de insumos' : 'Asignar insumos a vehículo'}
        </h3>
        <p className="text-sm text-stone-500">
          Registra un valor total asignado y descuenta stock solo para insumos de proveedores externos.
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div>
          <label className={labelCls}>Vehículo</label>
          <select
            name="vehiculo"
            value={form.vehiculo}
            onChange={handle}
            required
            disabled={loadingData}
            className={inputCls}
          >
            <option value="">
              {loadingData ? 'Cargando vehículos...' : 'Selecciona un vehículo'}
            </option>
            {vehiculos.map(vehiculo => (
              <option key={vehiculo.id} value={vehiculo.id}>
                {vehiculo.placa} · {vehiculo.marca} {vehiculo.modelo}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className={labelCls}>Fecha del registro</label>
          <input
            name="fechaValorTotalAsignado"
            type="datetime-local"
            value={form.fechaValorTotalAsignado}
            onChange={handle}
            required
            className={inputCls}
          />
        </div>

        <div>
          <label className={labelCls}>Valor total asignado</label>
          <input
            name="valorTotalAsignado"
            type="number"
            min="0"
            step="0.01"
            value={form.valorTotalAsignado}
            onWheel={preventNumberInputWheelChange}
            onChange={handle}
            required
            className={inputCls}
            placeholder="100.00"
          />
        </div>

        <div>
          <label className={labelCls}>Saldo disponible</label>
          <div
            className={`rounded-2xl border px-3 py-2.5 text-sm font-semibold ${
              resumen.saldoDisponible < 0
                ? 'border-rose-200 bg-rose-50 text-rose-700'
                : 'border-emerald-200 bg-emerald-50 text-emerald-700'
            }`}
          >
            {formatCurrency(resumen.saldoDisponible)}
          </div>
        </div>

        <div className="md:col-span-2">
          <label className={labelCls}>Observación</label>
          <textarea
            name="observacion"
            value={form.observacion}
            onChange={handle}
            rows={3}
            className={inputCls}
            placeholder="Observación opcional"
          />
        </div>

        <div>
          <label className={labelCls}>Responsable (opcional)</label>
          <input
            name="responsable"
            value={form.responsable}
            onChange={handle}
            className={inputCls}
            placeholder="Nombre del responsable"
          />
        </div>

        <div>
          <label className={labelCls}>Kilometraje (opcional)</label>
          <input
            name="kilometraje"
            type="number"
            min="0"
            step="0.01"
            value={form.kilometraje}
            onWheel={preventNumberInputWheelChange}
            onChange={handle}
            className={inputCls}
            placeholder="0.00"
          />
        </div>
      </div>

      <div className="rounded-[var(--radius-card)] border border-stone-200 bg-stone-50/70 p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <h4 className="font-semibold text-stone-900">Detalle de insumos</h4>
            <p className="text-sm text-stone-500">
              Si no ingresas precio unitario, se usará el precio actual del insumo.
            </p>
          </div>
          <button
            type="button"
            onClick={addDetalle}
            className="ui-btn ui-btn-info ui-btn-sm"
          >
            Agregar insumo
          </button>
        </div>

        <div className="mt-4 overflow-x-auto rounded-[16px] border border-stone-200">
          <table className="ui-tabla min-w-max text-xs text-left">
            <thead className="bg-stone-100 text-stone-500 uppercase tracking-wider">
              <tr>
                <th className="px-3 py-2 font-semibold border-b border-stone-200 min-w-[180px]">Insumo</th>
                <th className="px-3 py-2 font-semibold border-b border-stone-200 min-w-[90px]">Cantidad</th>
                <th className="px-3 py-2 font-semibold border-b border-stone-200 min-w-[110px]">Precio unitario</th>
                <th className="px-3 py-2 font-semibold border-b border-stone-200 min-w-[100px]">Total línea</th>
                <th className="px-3 py-2 font-semibold border-b border-stone-200 min-w-[80px]"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100 bg-white">
              {detalleSlice.map((detalle, sliceIndex) => {
                const realIndex = (detallePageSafe - 1) * DETALLE_PAGE_SIZE + sliceIndex;
                const insumo = insumosById.get(detalle.insumo);
                const totalLinea = getDetalleTotal(detalle);

                return (
                  <tr key={`${realIndex}-${detalle.insumo || 'linea'}`} className="hover:bg-stone-50 transition-colors">
                    <td className="p-1 border-r border-stone-100">
                      <select
                        value={detalle.insumo}
                        onChange={event => updateDetalle(realIndex, 'insumo', event.target.value)}
                        required
                        disabled={loadingData}
                        className="w-full bg-transparent px-2 py-1.5 outline-none text-xs rounded"
                      >
                        <option value="">
                          {loadingData ? 'Cargando...' : 'Selecciona un insumo'}
                        </option>
                        {insumos.map(item => (
                          <option key={item.id} value={item.id}>
                            {item.nombre} · {formatCurrency(item.precio)}
                          </option>
                        ))}
                      </select>
                      {insumo && (
                        <p className="mt-0.5 px-2 text-[10px] text-stone-400 leading-tight">
                          Prov: {insumo.proveedorNombre || '—'} · Stock: {formatQuantity(insumo.stockDisponible)}
                          {insumo.proveedorEsPropia ? ' (propio)' : ' (externo)'}
                        </p>
                      )}
                    </td>
                    <td className="p-1 border-r border-stone-100">
                      <input
                        type="number"
                        min="0.01"
                        step="0.01"
                        value={detalle.cantidad}
                        onWheel={preventNumberInputWheelChange}
                        onChange={event => updateDetalle(realIndex, 'cantidad', event.target.value)}
                        required
                        className="w-full bg-transparent px-2 py-1.5 outline-none focus:bg-white focus:ring-1 focus:ring-stone-300 rounded text-xs"
                      />
                    </td>
                    <td className="p-1 border-r border-stone-100">
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={detalle.precioUnitario}
                        onWheel={preventNumberInputWheelChange}
                        onChange={event => updateDetalle(realIndex, 'precioUnitario', event.target.value)}
                        className="w-full bg-transparent px-2 py-1.5 outline-none focus:bg-white focus:ring-1 focus:ring-stone-300 rounded text-xs"
                        placeholder={insumo ? String(insumo.precio) : '0.00'}
                      />
                    </td>
                    <td className="p-2 font-semibold text-stone-800 text-xs border-r border-stone-100">
                      {formatCurrency(totalLinea)}
                    </td>
                    <td className="p-2">
                      <button
                        type="button"
                        onClick={() => removeDetalle(realIndex)}
                        disabled={detalles.length === 1}
                        className="rounded-lg border border-rose-200 bg-rose-50 px-2 py-1 text-[11px] font-semibold text-rose-700 transition hover:bg-rose-100 disabled:opacity-50"
                      >
                        Quitar
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {detalles.length > DETALLE_PAGE_SIZE && (
            <div className="flex items-center justify-between border-t border-stone-200 bg-stone-50 px-4 py-2">
              <span className="text-xs text-stone-500">
                {detalles.length} insumo(s) · Pág {detallePageSafe} de {detalleTotalPages}
              </span>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setDetallePage(p => Math.max(1, p - 1))}
                  disabled={detallePageSafe <= 1}
                  className="rounded-xl border border-stone-200 bg-white px-2.5 py-1 text-xs font-semibold text-stone-700 transition hover:bg-stone-50 disabled:opacity-50"
                >
                  ← Anterior
                </button>
                <button
                  type="button"
                  onClick={() => setDetallePage(p => Math.min(detalleTotalPages, p + 1))}
                  disabled={detallePageSafe >= detalleTotalPages}
                  className="rounded-xl border border-stone-200 bg-white px-2.5 py-1 text-xs font-semibold text-stone-700 transition hover:bg-stone-50 disabled:opacity-50"
                >
                  Siguiente →
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="mt-4 grid gap-3 md:grid-cols-3">
          <div className="rounded-[var(--radius-panel)] border border-stone-200 bg-white px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Valor asignado</p>
            <p className="mt-2 text-lg font-semibold text-stone-900">{formatCurrency(resumen.totalAsignado)}</p>
          </div>
          <div className="rounded-[var(--radius-panel)] border border-stone-200 bg-white px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Total insumos</p>
            <p className="mt-2 text-lg font-semibold text-stone-900">{formatCurrency(resumen.totalInsumos)}</p>
          </div>
          <div className="rounded-[var(--radius-panel)] border border-stone-200 bg-white px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">Saldo</p>
            <p className={`mt-2 text-lg font-semibold ${resumen.saldoDisponible < 0 ? 'text-rose-700' : 'text-emerald-700'}`}>
              {formatCurrency(resumen.saldoDisponible)}
            </p>
          </div>
        </div>
      </div>

      {insumos.length === 0 && !loadingData && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
          No hay insumos registrados. Primero crea insumos en el módulo de bodega.
        </div>
      )}

      {error && (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="ui-btn ui-btn-secondary"
        >
          Cancelar
        </button>
        <button
          type="submit"
          disabled={loading || loadingData || insumos.length === 0 || resumen.saldoDisponible < 0}
          className="rounded-2xl bg-teal-700 px-4 py-2 text-sm font-semibold text-white transition hover:bg-teal-600 disabled:opacity-60"
        >
          {loading ? 'Guardando...' : isEditing ? 'Guardar cambios' : 'Guardar asignación'}
        </button>
      </div>
    </form>
  );
}
